import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Avatar, Badge, Card, ErrorNotice, PageHeader, Spinner, Stat } from '@/components/ui';
import { AccessPanel } from '@/features/access/AccessPanel';
import { SignInPanel } from '@/features/access/SignInPanel';
import { DeleteUserButton } from '@/features/access/DeleteUserButton';
import { PersonalDetailsCard } from '@/features/employees/PersonalDetailsCard';
import { RegisterPanel } from '@/features/documents/RegisterPanel';
import { SendDocumentsDialog } from '@/features/documents/SendDocumentsDialog';
import { ReinviteButton } from '@/features/access/ReinviteButton';
import { AccountStatusBadge } from '@/features/access/AccountStatusBadge';
import { awaitingSignIn } from '@/features/access/signIn';
import { usePermissions } from '@/features/access/usePermissions';
import { useAuth } from '@/features/auth/AuthProvider';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { dateShort, duration, fullName, isoDate, time } from '@/lib/format';
import { useLabel } from '@/lib/useLabel';

export function EmployeeDetailPage() {
  const { t } = useI18n();
  const L = useLabel();
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const { email: myEmail } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);

  const user = useQuery({ queryKey: ['user', id], queryFn: () => api.user(id) });
  const shifts = useQuery({
    queryKey: ['shifts', { userId: id }],
    queryFn: () => api.shifts({ userId: id, limit: 30 }),
  });
  // The register, not the raw uploads: this has to show what is *missing* too.
  const register = useQuery({
    queryKey: ['worker-register', id],
    queryFn: () => api.workerRegister(id),
  });
  // The van they are driving, if any: asked about as often as where they are working.
  const driving = useQuery({
    queryKey: ['vehicles', 'held-by', id],
    queryFn: () => api.vehiclesHeldBy(id),
    enabled: can('vehicles', 'read'),
  });
  const assignments = useQuery({
    queryKey: ['assignments', { userId: id }],
    queryFn: () => api.assignments({ userId: id, from: isoDate(new Date()) }),
  });

  const toggleStatus = useMutation({
    mutationFn: (suspend: boolean) => (suspend ? api.suspendUser(id) : api.reactivateUser(id)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user', id] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  if (user.isLoading) return <Spinner />;
  if (user.error) return <ErrorNotice error={user.error} />;
  if (!user.data) return null;

  const u = user.data.user;
  const summary = register.data?.summary;
  const openAssignments = assignments.data?.assignments ?? [];
  const rows = shifts.data?.shifts ?? [];
  const suspended = u.status === 'suspended';

  const totalMinutes = rows
    .filter((s) => s.status !== 'auto_closed' && s.status !== 'disputed')
    .reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0);

  const flagged = rows.filter((s) => s.exceptionFlags.length > 0).length;

  return (
    <>
      <PageHeader
        title={fullName(u)}
        subtitle={u.email ?? t('access.noEmail')}
        actions={
          <>
            <Link to="/employees" className="btn-secondary">
              {t('common.backToList')}
            </Link>
            {can('compliance', 'write') ? (
              <button type="button" className="btn-secondary" onClick={() => setSending(true)}>
                {t('send.button')}
              </button>
            ) : null}
            {awaitingSignIn(u) && can('users', 'write') ? <ReinviteButton userId={u.id} /> : null}
            <button
              type="button"
              className={suspended ? 'btn-primary' : 'btn-danger'}
              disabled={toggleStatus.isPending}
              onClick={() => toggleStatus.mutate(!suspended)}
            >
              {t(suspended ? 'employees.reactivate' : 'employees.suspend')}
            </button>
            {can('users', 'write') && (u.email ?? '').toLowerCase() !== (myEmail ?? '').toLowerCase() ? (
              <DeleteUserButton user={u} onDeleted={() => navigate('/employees')} />
            ) : null}
          </>
        }
      />

      <SendDocumentsDialog open={sending} onClose={() => setSending(false)} initialUserIds={[u.id]} />

      <div className="flex items-center gap-4 mb-6">
        <Avatar name={fullName(u)} size={56} photoUrl={u.photoUrl} />
        <div className="flex gap-2">
          <Badge tone={L('role', u.role).tone}>{L('role', u.role).label}</Badge>
          <AccountStatusBadge user={u} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label={t('employees.hoursRecent')} value={duration(totalMinutes)} tone="primary" />
        <Stat label={t('employees.shiftsRecorded')} value={rows.length} />
        <Stat label={t('employees.withFlags')} value={flagged} tone={flagged > 0 ? 'warning' : 'neutral'} />
        <Stat
          label={t('employees.documentsToHandle')}
          value={(summary?.missing ?? 0) + (summary?.expired ?? 0) + (summary?.expiring ?? 0)}
          tone={
            (summary?.missing ?? 0) + (summary?.expired ?? 0) > 0
              ? 'danger'
              : (summary?.expiring ?? 0) > 0
                ? 'warning'
                : 'success'
          }
          hint={summary ? t('employees.ofExpected', { total: summary.total }) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/*
          Two columns that fill each other's space. The document file on the right is
          long (thirteen items), so the access card sits under the shifts on the left
          instead of below both — where it left a tall empty gap beside the documents
          and then ran at part width under everything.
        */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {/* The whole document file, filed from here. It was a read-only list with a
              link to another page, where an office account's file could not be opened
              at all — so "add this person's documents" had no place to happen. */}
          <section>
            <h2 className="font-bold mb-3">{t('employees.documentFile')}</h2>
            <RegisterPanel scope="worker" userId={u.id} />
          </section>

          <section>
            <h2 className="font-bold mb-3">{t('employees.recentShifts')}</h2>
            <Card padded={false}>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th className="th">{t('common.date')}</th>
                      <th className="th">{t('common.time')}</th>
                      <th className="th">{t('shifts.duration')}</th>
                      <th className="th">{t('shifts.flags')}</th>
                      <th className="th" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => (
                      <tr key={s.id} className="hover:bg-surface-alt">
                        <td className="td tabular whitespace-nowrap">{dateShort(s.clockInAt)}</td>
                        <td className="td tabular whitespace-nowrap">
                          {time(s.clockInAt)}
                          {s.clockOutAt ? ` – ${time(s.clockOutAt)}` : ' – …'}
                        </td>
                        <td className="td tabular font-semibold">{duration(s.durationMinutes)}</td>
                        <td className="td">
                          <div className="flex flex-wrap gap-1">
                            {s.exceptionFlags.map((f) => {
                              const spec = L('flag', f);
                              return (
                                <Badge key={f} tone={spec.tone}>
                                  {spec.label}
                                </Badge>
                              );
                            })}
                          </div>
                        </td>
                        <td className="td text-end">
                          <Link to={`/shifts/${s.id}`} className="td-action text-primary hover:underline">
                            {t('common.details')}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted">{t('employees.noShifts')}</div>
              ) : null}
            </Card>
          </section>

          {/* Who can see and do what for this person, on their own page — which is
              where someone asking "what can Marco see?" goes looking. Shown to anyone
              who may read the user list; editable only with users:write, which
              AccessPanel checks. */}
          {can('users', 'read') ? (
            <section>
              <h2 className="font-bold mb-3">{t('access.heading')}</h2>
              <Card>
                <AccessPanel user={u} isSelf={Boolean(u.email) && u.email!.toLowerCase() === (myEmail ?? '').toLowerCase()} />
              </Card>
            </section>
          ) : null}
        </div>

        <div className="space-y-6">
          <section>
            <h2 className="font-bold mb-3">{t('personal.heading')}</h2>
            <PersonalDetailsCard user={u} canEdit={can('employees', 'write')} />
          </section>

          {/* Signing in to the app by name: create it, or reset a forgotten password. */}
          {can('users', 'write') ? (
            <SignInPanel
              user={u}
              isSelf={Boolean(u.email) && u.email!.toLowerCase() === (myEmail ?? '').toLowerCase()}
            />
          ) : null}

          <div>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="font-bold">{t('employees.activeAssignments')}</h2>
              {openAssignments.length > 0 ? <Badge tone="primary">{openAssignments.length}</Badge> : null}
            </div>
            <Card padded={false}>
              {openAssignments.length === 0 ? (
                <div className="px-4 py-6 text-sm text-muted text-center">
                  {t('employees.noAssignments')}{' '}
                  <Link to="/assignments" className="text-primary font-semibold hover:underline">
                    {t('common.assign')}
                  </Link>
                </div>
              ) : (
                <ul>
                  {openAssignments.map((a) => (
                    <li key={a.id} className="px-4 py-3 border-b border-border last:border-0">
                      <div className="font-semibold text-sm">{a.siteName}</div>
                      <div className="text-xs text-muted tabular">
                        {a.endsOn
                          ? t('employees.assignmentRange', {
                              from: dateShort(a.startsOn),
                              to: dateShort(a.endsOn),
                            })
                          : t('employees.assignmentOpen', { from: dateShort(a.startsOn) })}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {can('vehicles', 'read') ? (
            <div>
              <h2 className="font-bold mb-3">{t('vehicles.drivingTitle')}</h2>
              <Card padded={false}>
                {(driving.data?.vehicles ?? []).length === 0 ? (
                  <div className="px-4 py-6 text-sm text-muted text-center">
                    {t('vehicles.drivingNone')}{' '}
                    <Link to="/vehicles" className="text-primary font-semibold hover:underline">
                      {t('nav.vehicles')}
                    </Link>
                  </div>
                ) : (
                  <ul>
                    {driving.data!.vehicles.map((v) => (
                      <li key={v.id} className="border-b border-border last:border-0">
                        <Link to={`/vehicles/${v.id}`} className="block px-4 py-3 hover:bg-surface-alt">
                          <span className="font-mono font-semibold">{v.plate}</span>
                          <span className="ms-2 text-sm text-muted">
                            {[v.make, v.model].filter(Boolean).join(' ') || v.name}
                          </span>
                          {v.holder?.since ? (
                            <span className="block text-xs text-muted">
                              {t('vehicles.since', { date: dateShort(v.holder.since) })}
                            </span>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          ) : null}

          <div>
            <h2 className="font-bold mb-3">{t('employees.privacy')}</h2>
            <Card>
              <p className="text-sm">
                {t('employees.locationNotice')}{' '}
                {u.locationNoticeAckAt ? (
                  <strong>{t('employees.noticeRead', { date: dateShort(u.locationNoticeAckAt) })}</strong>
                ) : (
                  <strong className="text-warning">{t('employees.noticeUnread')}</strong>
                )}
              </p>
              {/* Not consent — the legal basis is the employment contract and
                  legitimate interest. See docs/07 §2; the wording matters. */}
              <p className="text-xs text-subtle mt-2 leading-relaxed">{t('employees.noticeExplainer')}</p>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
